import { z } from "zod";
// From the leaf module, not the barrel: `index.ts` re-exports this file, so importing the
// barrel here would be a cycle, and `z.object` reads these at module-init time.
import { AccessPlaneSchema, AUTONOMY_LEVELS, AutonomyLevelSchema } from "./enums";

/**
 * The initial request categories and their category-specific follow-up fields.
 *
 * Architecture plan §7.1: "The form is also the first stage of the investigation... Field
 * design is card quality. Category-specific follow-up questions are worth more than a longer
 * generic form." Build plan Phase 0 ("Define initial request categories") records the list;
 * Phase 4 ("Category-specific follow-up fields. Card quality is decided here") renders it.
 *
 * Every field below exists because the card standard requires something exact on the card — an
 * identifier, verbatim error text, a portal path, or a stated prerequisite (§8.9). A field that
 * does not feed one of those makes a longer form, not a better card.
 *
 * Nothing here is settled. The list, the flags, and the wording are proposed — ratify in the
 * Phase 0 worksheet; the reasoning is in `Docs/phase-0/request-categories.md`.
 */

/** Kebab-case, because ids appear in URLs, board tags, and configuration keys. */
export const CATEGORY_ID_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * §8.4: "Never auto-merge on similarity alone for access, identity, HR, or device-specific
 * requests... Similarity may propose a merge for those categories; only a human may accept it."
 * `agent-may-reuse` is therefore reserved for categories where two similar reports are genuinely
 * one shared incident — a shared printer queue, an office network, a tenant-wide service.
 */
export const MERGE_POLICIES = ["agent-may-reuse", "human-confirms-merge"] as const;
export const MergePolicySchema = z.enum(MERGE_POLICIES);
export type MergePolicy = z.infer<typeof MergePolicySchema>;

/**
 * The identifier kinds §8.2 extracts from a description. These become the inline-code
 * identifiers the card standard requires; the follow-up fields are what make them present.
 */
export const IDENTIFIER_KINDS = [
  "upn",
  "displayName",
  "deviceSerial",
  "deviceName",
  "applicationVersion",
  "groupName",
  "server",
  "printer",
  "path",
  "errorCode",
  "errorText",
] as const;
export const IdentifierKindSchema = z.enum(IDENTIFIER_KINDS);
export type IdentifierKind = z.infer<typeof IdentifierKindSchema>;

/**
 * The wiki pages the plan names by slug (§8.6). Only these four are named, so only these four
 * are hinted; every other category carries an empty list until the corpus is published and
 * §8.6's gap reporting says which SOP to write next. The agent still searches the whole
 * `wiki-seed` corpus — a hint is a starting point, never a restriction, and never a substitute
 * for the citation the agent must produce itself.
 */
export const WIKI_SOP_SLUGS = [
  "Password-Reset-SOP",
  "VPN-Setup-SOP",
  "Mac-Provisioning-SOP",
  "Resolving-Set-Up-Like-Requests",
] as const;
export const WikiSopSlugSchema = z.enum(WIKI_SOP_SLUGS);
export type WikiSopSlug = z.infer<typeof WikiSopSlugSchema>;

/**
 * `identifier` is a text input whose answer will be rendered in inline code on the card — a UPN,
 * a serial, a group, a server, an application and version. The type is a rendering and
 * validation contract, not a hint: an identifier answer is quoted exactly, never paraphrased.
 */
export const FOLLOW_UP_FIELD_TYPES = [
  "text",
  "textarea",
  "select",
  "date",
  "boolean",
  "identifier",
] as const;
export const FollowUpFieldTypeSchema = z.enum(FOLLOW_UP_FIELD_TYPES);
export type FollowUpFieldType = z.infer<typeof FollowUpFieldTypeSchema>;

const FollowUpFieldObject = z.object({
  id: z.string().regex(CATEGORY_ID_PATTERN, "expected kebab-case"),
  label: z.string().min(1),
  type: FollowUpFieldTypeSchema,
  required: z.boolean(),
  options: z.array(z.string().min(1)).min(2).readonly().optional(),
  placeholder: z.string().min(1).optional(),
  /** Which card-standard requirement this field feeds. A field without one is form bloat. */
  whyItMatters: z.string().min(1),
});

export const FollowUpFieldSchema = FollowUpFieldObject.superRefine((field, ctx) => {
  if (field.type === "select" && field.options === undefined) {
    ctx.addIssue({ code: "custom", message: "a select field needs options", path: ["options"] });
  }
  if (field.type !== "select" && field.options !== undefined) {
    ctx.addIssue({
      code: "custom",
      message: "options are only meaningful on a select field",
      path: ["options"],
    });
  }
});
export type FollowUpField = z.infer<typeof FollowUpFieldSchema>;

const RequestCategoryObject = z.object({
  id: z.string().regex(CATEGORY_ID_PATTERN, "expected kebab-case"),
  label: z.string().min(1),
  /** One sentence, requester-facing, second person. It appears beside the choice on the form. */
  description: z.string().min(1),
  /**
   * The plane this category's work usually sits on (§8.5). It is not cosmetic — it decides the
   * Epic parent and the prerequisites the card must state. At category level `unknown` does not
   * mean the agent failed to classify; it means the category alone cannot decide, so the agent
   * classifies per request from the follow-up answers — see `device-cannot-reach-sign-in`, where
   * the deciding field is "Where the device is". A per-request `unknown` is still a stop (§8.12).
   */
  accessPlane: AccessPlaneSchema,
  /**
   * §7.7: the portal requires an Entra sign-in, so the account-access classes cannot arrive
   * through it. They remain categories because a call-taker raises them on the caller's behalf
   * (§11.3) — the category exists for the staff-raised route, not for the public form.
   */
  portalReceivable: z.boolean(),
  phoneRouteReason: z.string().min(1).optional(),
  /** §8.12 stop conditions: permission or administrator work, anything privileged or destructive. */
  alwaysEscalate: z.boolean(),
  escalationReason: z.string().min(1).optional(),
  mergePolicy: MergePolicySchema,
  /** §8.12: "New categories always start at L1." Enforced below and again in the tests. */
  defaultAutonomy: AutonomyLevelSchema,
  followUpFields: z.array(FollowUpFieldSchema).min(3).max(7).readonly(),
  wikiSopHints: z.array(WikiSopSlugSchema).readonly(),
  identifiersExpected: z.array(IdentifierKindSchema).min(1).readonly(),
  /**
   * §8.5 reserves Priority 1 for the immediate unblocker, a time-sensitive revocation, or a
   * critical access or recovery gate. A requester never sets priority; this only marks where
   * triage should expect it and where §8.12 will therefore stop and escalate.
   */
  p1Candidate: z.boolean().optional(),
});

export const RequestCategorySchema = RequestCategoryObject.superRefine((category, ctx) => {
  if (!category.portalReceivable && category.phoneRouteReason === undefined) {
    ctx.addIssue({
      code: "custom",
      message: "a category the portal cannot receive must say why the phone is the route (§7.7)",
      path: ["phoneRouteReason"],
    });
  }
  if (category.portalReceivable && category.phoneRouteReason !== undefined) {
    ctx.addIssue({
      code: "custom",
      message: "phoneRouteReason belongs only to a category the portal cannot receive",
      path: ["phoneRouteReason"],
    });
  }
  if (category.alwaysEscalate && category.escalationReason === undefined) {
    ctx.addIssue({
      code: "custom",
      message: "an always-escalate category must cite its §8.12 stop condition",
      path: ["escalationReason"],
    });
  }
  if (!category.alwaysEscalate && category.escalationReason !== undefined) {
    ctx.addIssue({
      code: "custom",
      message: "escalationReason belongs only to an always-escalate category",
      path: ["escalationReason"],
    });
  }
  // A category the agent must hand to a human is not one the agent may merge on similarity.
  if (category.alwaysEscalate && category.mergePolicy !== "human-confirms-merge") {
    ctx.addIssue({
      code: "custom",
      message: "an always-escalate category cannot be agent-may-reuse (§8.4, §8.12)",
      path: ["mergePolicy"],
    });
  }
  const seen = new Set<string>();
  for (const field of category.followUpFields) {
    if (seen.has(field.id)) {
      ctx.addIssue({
        code: "custom",
        message: `duplicate follow-up field id ${field.id}`,
        path: ["followUpFields"],
      });
    }
    seen.add(field.id);
  }
});
export type RequestCategory = z.infer<typeof RequestCategorySchema>;

/** Every category starts here (§8.12). Graduation to L2 is on the numeric evidence in §13.2. */
const INITIAL_AUTONOMY = AUTONOMY_LEVELS[1];

/**
 * The sentence the published guide uses to send an account-access caller to the phone (§7.7).
 * It is one sentence, in the requester's words, and it is the same on the form, on the static
 * status page, and in the quick guide — a caller who cannot sign in has to recognise it from
 * whichever one they reach.
 */
export const PHONE_ROUTE_SENTENCE =
  "If you cannot sign in — a forgotten or expired password, a multi-factor prompt that will not work, a locked or disabled account, or a device that will not load the sign-in page — call the support number instead: the portal needs you signed in, and the person who answers will raise the request for you.";

/** Shared because a privileged identity change records verification the same way every time. */
const VERIFICATION_OPTIONS = [
  "Known personally to the call-taker",
  "Manager confirmation",
  "Callback to the number on record",
  "Other — described on the call",
] as const;

export const REQUEST_CATEGORIES = [
  {
    id: "password-account-access",
    label: "Password or sign-in failure",
    description:
      "You cannot sign in because your password is forgotten, expired, or is being rejected.",
    accessPlane: "mixed",
    portalReceivable: false,
    phoneRouteReason:
      "The portal requires an Entra token and a rejected password is exactly what prevents one. §7.7 makes the phone the published route; §11.3 has the call-taker raise the request with provisional UPN binding.",
    alwaysEscalate: false,
    mergePolicy: "human-confirms-merge",
    defaultAutonomy: INITIAL_AUTONOMY,
    followUpFields: [
      {
        id: "caller-upn",
        label: "Caller's sign-in address",
        type: "identifier",
        required: true,
        placeholder: "first.last@example.org",
        whyItMatters:
          "The affected account is the identifier the card renders in inline code, and it is the value §11.3 stores as the provisional UPN binding so the caller sees the request once they can sign in again.",
      },
      {
        id: "verification-method",
        label: "How the caller was verified",
        type: "select",
        required: true,
        options: VERIFICATION_OPTIONS,
        whyItMatters:
          "A password reset is an identity action. The gate asks whether prerequisites and approvals are explicit; how the caller proved who they were is that prerequisite, and it belongs on the card before the reset rather than in someone's memory after it.",
      },
      {
        id: "sign-in-surface",
        label: "Where the password is being rejected",
        type: "select",
        required: true,
        options: [
          "Windows sign-in",
          "Mac login",
          "Microsoft 365 or Outlook on the web",
          "VPN client",
          "Another application",
        ],
        whyItMatters:
          "Separates a directory password from a cached credential on one device, which decides the access plane and the portal path the card has to name — and §8.6 forbids guessing that path.",
      },
      {
        id: "exact-error-text",
        label: "The exact wording on screen",
        type: "textarea",
        required: false,
        whyItMatters:
          "Verbatim error text is what the standard means by exact. It is what distinguishes an expired password from a locked account before anyone touches the account.",
      },
      {
        id: "callback-number",
        label: "Best number to call back",
        type: "text",
        required: true,
        whyItMatters:
          "The phone is the only channel to a caller who cannot sign in, so it is also the only way to record the completion the card requires.",
      },
    ],
    wikiSopHints: ["Password-Reset-SOP"],
    identifiersExpected: ["upn", "displayName", "deviceName", "errorText"],
  },
  {
    id: "mfa-duo",
    label: "Multi-factor or Duo prompt",
    description:
      "Your multi-factor prompt does not arrive, is refused, or you have a new phone to enrol.",
    accessPlane: "cloud",
    portalReceivable: false,
    phoneRouteReason:
      "Multi-factor is part of the sign-in the portal depends on, so a broken or unenrolled second factor blocks the form itself (§7.7). The call-taker raises it.",
    alwaysEscalate: false,
    mergePolicy: "human-confirms-merge",
    defaultAutonomy: INITIAL_AUTONOMY,
    followUpFields: [
      {
        id: "caller-upn",
        label: "Caller's sign-in address",
        type: "identifier",
        required: true,
        whyItMatters:
          "The enrolment being repaired belongs to one account; the card names it exactly rather than describing the person.",
      },
      {
        id: "mfa-situation",
        label: "What is happening",
        type: "select",
        required: true,
        options: [
          "The prompt never arrives",
          "The prompt is refused or times out",
          "New phone — the enrolment needs moving",
          "Never enrolled",
          "The phone is lost or stolen",
        ],
        whyItMatters:
          "These are four different pieces of work and one security incident. A lost or stolen device is a time-sensitive revocation, which §8.5 reserves Priority 1 for, so the answer changes the card rather than decorating it.",
      },
      {
        id: "phone-changed",
        label: "Has the phone or number changed since this last worked?",
        type: "boolean",
        required: true,
        whyItMatters:
          "A changed device is both the commonest cause and its own prerequisite: the old enrolment has to be removed before the new one is added, and the card must say so.",
      },
      {
        id: "exact-error-text",
        label: "The exact wording on screen",
        type: "textarea",
        required: false,
        whyItMatters:
          "Verbatim text separates a blocked sign-in from a refused prompt, which are different fixes on different planes.",
      },
      {
        id: "last-worked",
        label: "When this last worked",
        type: "text",
        required: false,
        placeholder: "This morning, last Friday, never",
        whyItMatters:
          "Onset bounds the change window a technician has to search, and the standard asks that a card be executable without going back to the requester for it.",
      },
    ],
    wikiSopHints: ["VPN-Setup-SOP"],
    identifiersExpected: ["upn", "deviceName", "errorText"],
  },
  {
    id: "account-locked-disabled",
    label: "Locked or disabled account",
    description:
      "Your account is locked out or has been disabled, so nothing accepts your sign-in at all.",
    accessPlane: "mixed",
    portalReceivable: false,
    phoneRouteReason:
      "A locked or disabled account cannot obtain a token, so the portal cannot receive the request that asks for it to be unlocked (§7.7).",
    alwaysEscalate: true,
    escalationReason:
      "Unlocking or re-enabling an account is administrator work on an identity, and a disabled account may be a deliberate leaver or security action rather than a fault. §8.12 stops the agent on privileged identity changes; re-enabling one on the strength of a phone call is exactly the guess it must not make.",
    mergePolicy: "human-confirms-merge",
    defaultAutonomy: INITIAL_AUTONOMY,
    followUpFields: [
      {
        id: "caller-upn",
        label: "Caller's sign-in address",
        type: "identifier",
        required: true,
        whyItMatters:
          "The account is the subject of the card and the thing the audit test is written against, so it must be exact.",
      },
      {
        id: "lockout-message",
        label: "The exact wording on screen",
        type: "textarea",
        required: true,
        whyItMatters:
          "Locked, disabled, and unlicensed produce three different messages and three different pieces of work. Without the wording the card guesses which one it is.",
      },
      {
        id: "recent-account-change",
        label: "Anything that changed recently",
        type: "select",
        required: true,
        options: [
          "Recently left or changed role",
          "Returned from leave",
          "Password changed recently",
          "Nothing changed that I know of",
        ],
        whyItMatters:
          "This is the check that stops a leaver's account being re-enabled as a fault. It is the prerequisite a reviewer needs before approving a privileged change.",
      },
      {
        id: "verification-method",
        label: "How the caller was verified",
        type: "select",
        required: true,
        options: VERIFICATION_OPTIONS,
        whyItMatters:
          "A privileged identity change needs the verification recorded on the card as an explicit prerequisite, not held in the call-taker's memory.",
      },
    ],
    wikiSopHints: ["Password-Reset-SOP"],
    identifiersExpected: ["upn", "displayName", "errorText"],
  },
  {
    id: "device-cannot-reach-sign-in",
    label: "The device will not reach the sign-in page",
    description:
      "Your computer cannot load the sign-in page at all — nothing appears, so no account is being rejected.",
    accessPlane: "unknown",
    portalReceivable: false,
    phoneRouteReason:
      "A device that cannot reach a sign-in page cannot reach the portal either, whatever the cause. §7.7 lists this as the fourth class the portal can never receive.",
    alwaysEscalate: false,
    mergePolicy: "human-confirms-merge",
    defaultAutonomy: INITIAL_AUTONOMY,
    followUpFields: [
      {
        id: "device-name-or-serial",
        label: "Device name or serial number",
        type: "identifier",
        required: true,
        whyItMatters:
          "With no account in play the device is the only subject the card has, and the standard requires it exactly rather than as “the user's laptop”.",
      },
      {
        id: "connecting-from",
        label: "Where the device is",
        type: "select",
        required: true,
        options: [
          "Hoffman office — wired",
          "Hoffman office — Wi-Fi",
          "Home or remote",
          "Travelling or on a public network",
        ],
        whyItMatters:
          "This is the answer that classifies the access plane. The same symptom is a LAN problem in the office and an internet problem at home, and §8.5 makes the plane decide the Epic parent and the prerequisites.",
      },
      {
        id: "what-the-screen-shows",
        label: "What the screen shows, word for word",
        type: "textarea",
        required: true,
        whyItMatters:
          "A certificate warning, a name-resolution failure, and a blank page are three different cards. Verbatim text is the only evidence available before someone is in front of the device.",
      },
      {
        id: "others-affected",
        label: "Who else is affected",
        type: "select",
        required: true,
        options: ["Only me", "Others near me", "A whole floor or office", "Unknown"],
        whyItMatters:
          "Normalizes to the individual / multiple people / company-wide impact statement §8.2 requires, and separates one broken device from an outage that belongs on a different card entirely.",
      },
    ],
    wikiSopHints: [],
    identifiersExpected: ["deviceName", "deviceSerial", "path", "errorText"],
  },
  {
    id: "vpn-remote-access",
    label: "VPN or remote access",
    description:
      "You cannot connect to the VPN, or cannot reach an internal system while working away from the office.",
    accessPlane: "internal",
    portalReceivable: true,
    alwaysEscalate: false,
    mergePolicy: "human-confirms-merge",
    defaultAutonomy: INITIAL_AUTONOMY,
    followUpFields: [
      {
        id: "vpn-client-and-version",
        label: "VPN client and version",
        type: "identifier",
        required: true,
        whyItMatters:
          "Application and version is one of the identifiers §8.2 extracts, and the SOP's steps and menu labels differ between versions — a card naming the wrong menu sends a technician to a screen that does not exist.",
      },
      {
        id: "connecting-from",
        label: "The network you are connecting from",
        type: "select",
        required: true,
        options: [
          "Home or remote network",
          "Hoffman office Wi-Fi",
          "Public or hotel Wi-Fi",
          "Mobile hotspot",
        ],
        whyItMatters:
          "`VPN-Setup-SOP` records that connecting from office Wi-Fi produces a hairpin false failure. This field is what stops a card being written for a fault that is not there.",
      },
      {
        id: "duo-prompt-received",
        label: "Did a multi-factor prompt appear?",
        type: "boolean",
        required: true,
        whyItMatters:
          "The SOP names Duo enrolment as the VPN prerequisite. The gate asks whether prerequisites are explicit, and a VPN card written without this one is really an enrolment card.",
      },
      {
        id: "exact-error-text",
        label: "The exact wording of the error",
        type: "textarea",
        required: true,
        whyItMatters:
          "Verbatim client error text is the identifier for this category; an authentication failure and a tunnel failure read almost the same once they are paraphrased.",
      },
      {
        id: "internal-resource-needed",
        label: "The internal server or share you need to reach",
        type: "identifier",
        required: false,
        whyItMatters:
          "Names the internal-plane resource the outcome depends on, so the card states a destination rather than “the network” and the functional test has something to check.",
      },
      {
        id: "first-time-or-regression",
        label: "Has this ever worked on this device?",
        type: "select",
        required: true,
        options: ["It has never worked on this device", "It worked until recently"],
        whyItMatters:
          "A first-time setup and a regression are different work packages under §8.7 — one is provisioning, the other a fault — and that shape decision is made before drafting, not after.",
      },
    ],
    wikiSopHints: ["VPN-Setup-SOP"],
    identifiersExpected: ["upn", "deviceName", "applicationVersion", "server", "errorText"],
  },
  {
    id: "email-microsoft-365",
    label: "Email and Microsoft 365",
    description:
      "Outlook, Teams, OneDrive, SharePoint, or another Microsoft 365 service is not behaving as it should.",
    accessPlane: "cloud",
    portalReceivable: true,
    alwaysEscalate: false,
    mergePolicy: "agent-may-reuse",
    defaultAutonomy: INITIAL_AUTONOMY,
    followUpFields: [
      {
        id: "service-affected",
        label: "Which service",
        type: "select",
        required: true,
        options: [
          "Outlook or email",
          "Teams",
          "OneDrive",
          "SharePoint",
          "Word, Excel, or PowerPoint",
          "Access to a shared mailbox or someone else's calendar",
        ],
        whyItMatters:
          "It picks the cloud portal path the card must name. The last option is not a fault at all — it is permission work, and it routes to the §8.12 escalation path instead of a mail-flow fix.",
      },
      {
        id: "client-and-version",
        label: "Application and version",
        type: "identifier",
        required: true,
        placeholder: "From Help then About, or the app's About screen",
        whyItMatters:
          "Application and version is an §8.2 identifier and what makes a card reproducible; “Outlook” alone spans a decade of different behaviour.",
      },
      {
        id: "web-client-also-affected",
        label: "Does the same thing happen in a browser at office.com?",
        type: "boolean",
        required: true,
        whyItMatters:
          "One question that separates a device-specific client fault from a tenant-wide service fault. It decides whether the card is about one machine or about everyone, and therefore whether it may be merged with another report at all.",
      },
      {
        id: "exact-error-text",
        label: "The exact wording of the error",
        type: "textarea",
        required: true,
        whyItMatters:
          "Microsoft 365 errors carry correlation IDs and codes that are the fastest route to a cause. They are allowlisted identifier shapes at intake, so they can be pasted exactly.",
      },
      {
        id: "item-reference",
        label: "The message subject or file name involved",
        type: "identifier",
        required: false,
        whyItMatters:
          "Gives a technician something to reproduce against by name, without copying the content itself into the portal.",
      },
    ],
    wikiSopHints: [],
    identifiersExpected: ["upn", "applicationVersion", "path", "errorCode", "errorText"],
  },
  {
    id: "file-share-permissions",
    label: "File share or folder access",
    description:
      "You cannot open a folder, share, or site that you believe you should have access to.",
    accessPlane: "internal",
    portalReceivable: true,
    alwaysEscalate: true,
    escalationReason:
      "Permission and administrator work. §8.12 stops the agent because the standard requires a baseline export and an effective-permission comparison distinguishing direct, group-derived, eligible, scoped, and expired access — none of which can be derived from a request — and cloning privileged access is explicitly forbidden.",
    mergePolicy: "human-confirms-merge",
    defaultAutonomy: INITIAL_AUTONOMY,
    followUpFields: [
      {
        id: "exact-path",
        label: "The full path of the folder or site",
        type: "identifier",
        required: true,
        placeholder: "\\\\server\\share\\folder, or the full site address",
        whyItMatters:
          "§8.6 forbids inventing a path. The exact UNC path or site URL is the identifier the card renders in inline code, and the one thing a technician cannot guess.",
      },
      {
        id: "access-needed",
        label: "The access you need",
        type: "select",
        required: true,
        options: ["Read only", "Read and write", "I had access before and lost it"],
        whyItMatters:
          "The requested outcome has to be unambiguous. Read and write are different grants with different approvals, and lost access is a different investigation from new access.",
      },
      {
        id: "sponsor-or-approver",
        label: "Who approves this access",
        type: "text",
        required: true,
        whyItMatters:
          "A named approver is the prerequisite the gate looks for. Permission work with no approver on the card cannot be executed, only chased.",
      },
      {
        id: "exact-denial-text",
        label: "The exact wording of the denial",
        type: "textarea",
        required: true,
        whyItMatters:
          "An access denial and a path that no longer exists look identical to a requester and are opposite pieces of work; the verbatim message is what tells them apart.",
      },
      {
        id: "group-name-if-known",
        label: "The security group, if you know it",
        type: "identifier",
        required: false,
        whyItMatters:
          "A named group is an §8.2 identifier and lets the card state the exact membership change rather than “grant access to the folder”.",
      },
      {
        id: "colleague-with-correct-access",
        label: "A colleague whose access is correct, for reference",
        type: "text",
        required: false,
        whyItMatters:
          "Used to locate the right group, never to copy an account — the standard forbids cloning access, and the card says so wherever this field is answered.",
      },
    ],
    wikiSopHints: [],
    identifiersExpected: ["upn", "path", "groupName", "server", "errorText"],
  },
  {
    id: "software-install-licence",
    label: "Software install or licence",
    description:
      "You need an application installed, upgraded, or licensed — Adobe, a font, or anything else.",
    accessPlane: "mixed",
    portalReceivable: true,
    alwaysEscalate: false,
    mergePolicy: "human-confirms-merge",
    defaultAutonomy: INITIAL_AUTONOMY,
    followUpFields: [
      {
        id: "application-and-version",
        label: "Application and version needed",
        type: "identifier",
        required: true,
        whyItMatters:
          "Application and version is an §8.2 identifier, and the version decides the licence, the installer, and which plane the work sits on.",
      },
      {
        id: "device-name-or-serial",
        label: "The device it goes on",
        type: "identifier",
        required: true,
        whyItMatters:
          "An install is device-specific work; naming the machine exactly is what lets the completion record prove the right one was touched.",
      },
      {
        id: "business-justification",
        label: "What you need it for",
        type: "textarea",
        required: true,
        whyItMatters:
          "A licence is a purchase. The approval prerequisite and the acceptance criteria are written from the reason in the requester's own words, not from the application name.",
      },
      {
        id: "sponsor-or-approver",
        label: "Who approves the cost",
        type: "text",
        required: true,
        whyItMatters:
          "A named approver is the explicit prerequisite the gate checks for before anything is bought or assigned.",
      },
      {
        id: "existing-licence",
        label: "Do you already have this licensed on another device?",
        type: "boolean",
        required: false,
        whyItMatters:
          "A reassignment and a purchase are different shapes under §8.7 — one is a Task, the other needs approval and a lead time — and this answer decides which before the card is drafted.",
      },
      {
        id: "needed-by",
        label: "Needed by",
        type: "date",
        required: false,
        whyItMatters:
          "The needed-by date is what lets triage prioritize without the requester setting priority, which §8.5 does not allow.",
      },
    ],
    wikiSopHints: [],
    identifiersExpected: ["upn", "deviceName", "deviceSerial", "applicationVersion", "errorText"],
  },
  {
    id: "device-hardware",
    label: "Computer hardware fault",
    description:
      "Your Mac or PC has a physical fault — it will not power on, charge, display, or stay connected.",
    accessPlane: "mixed",
    portalReceivable: true,
    alwaysEscalate: false,
    mergePolicy: "human-confirms-merge",
    defaultAutonomy: INITIAL_AUTONOMY,
    followUpFields: [
      {
        id: "device-serial",
        label: "Device serial number",
        type: "identifier",
        required: true,
        whyItMatters:
          "The serial is the identifier warranty, enrolment, and the completion record are all keyed on. A device name is not enough to order a part against.",
      },
      {
        id: "device-type",
        label: "What kind of device",
        type: "select",
        required: true,
        options: [
          "Mac laptop",
          "Mac desktop",
          "Windows laptop",
          "Windows desktop",
          "Monitor or dock",
          "Another peripheral",
        ],
        whyItMatters:
          "Decides the vendor path and therefore the plane and the Epic parent; a Mac takes a different repair route from a Windows desktop.",
      },
      {
        id: "symptom-sequence",
        label: "What happens, in order, from pressing the power button",
        type: "textarea",
        required: true,
        whyItMatters:
          "Observable behaviour in sequence is what lets each child Task be one observable action, which is a gate check. “It is broken” cannot be turned into one.",
      },
      {
        id: "local-data-at-risk",
        label: "Is there work on this device that is not in OneDrive or on a share?",
        type: "boolean",
        required: true,
        whyItMatters:
          "A repair or reimage is destructive if local data was never captured. The gate asks for a safe before-state capture, and this is the field that tells the card whether one is needed.",
      },
      {
        id: "loaner-needed",
        label: "Do you need a loaner while it is repaired?",
        type: "boolean",
        required: true,
        whyItMatters:
          "A loaner is a separate observable action with its own lead time; the card states the dependency instead of discovering it once the device is already away.",
      },
    ],
    wikiSopHints: [],
    identifiersExpected: ["deviceSerial", "deviceName", "displayName", "errorText"],
  },
  {
    id: "mac-provisioning",
    label: "New Mac setup",
    description: "A new or replacement Mac needs to be enrolled, configured, and handed over.",
    accessPlane: "cloud",
    portalReceivable: true,
    alwaysEscalate: false,
    mergePolicy: "human-confirms-merge",
    defaultAutonomy: INITIAL_AUTONOMY,
    followUpFields: [
      {
        id: "device-serial",
        label: "Device serial number",
        type: "identifier",
        required: true,
        whyItMatters:
          "Enrolment is keyed on the serial, and the card cannot state the Apple Business Manager or Kandji prerequisite without it.",
      },
      {
        id: "user-upn",
        label: "Sign-in address of the person who will use it",
        type: "identifier",
        required: true,
        whyItMatters:
          "The account the device binds to is an exact identifier; assigning a Mac to the wrong one is precisely the rework the standard exists to prevent.",
      },
      {
        id: "applications-needed",
        label: "Applications this person needs",
        type: "textarea",
        required: true,
        whyItMatters:
          "The blueprint check needs an explicit list, and §8.6 forbids inventing one — so a card without it becomes an identification Task rather than an executable setup.",
      },
      {
        id: "set-up-like-whom",
        label: "“Set up like…” — name the person, if that is how you would describe it",
        type: "text",
        required: false,
        whyItMatters:
          "`Resolving-Set-Up-Like-Requests` exists because this phrasing has already caused rework. Naming the person lets the card resolve the ambiguity into an explicit application and access list; it is never a licence to copy an account.",
      },
      {
        id: "needed-by",
        label: "Needed by",
        type: "date",
        required: true,
        whyItMatters:
          "A start date is the hard constraint on provisioning; the card's stop condition and acceptance criteria are written against it.",
      },
      {
        id: "handover-method",
        label: "How the device reaches its user",
        type: "select",
        required: true,
        options: ["Collect from the office", "Ship to a home address", "Hand over on site"],
        whyItMatters:
          "Shipping changes the enrolment sequence and adds a lead time, so it is a prerequisite the card must state rather than an arrangement made afterwards.",
      },
    ],
    wikiSopHints: ["Mac-Provisioning-SOP", "Resolving-Set-Up-Like-Requests"],
    identifiersExpected: ["upn", "displayName", "deviceSerial", "groupName", "applicationVersion"],
  },
  {
    id: "printer",
    label: "Printing",
    description: "A printer will not print, is missing from your list, or is showing an error.",
    accessPlane: "internal",
    portalReceivable: true,
    alwaysEscalate: false,
    mergePolicy: "agent-may-reuse",
    defaultAutonomy: INITIAL_AUTONOMY,
    followUpFields: [
      {
        id: "printer-name",
        label: "Printer or queue name, exactly as it appears",
        type: "identifier",
        required: true,
        whyItMatters:
          "A shared printer is a shared incident. The exact queue name is what lets the equivalence search merge two reports of one outage into one card instead of opening two — it is why this category can be agent-may-reuse at all.",
      },
      {
        id: "printer-location",
        label: "Where the printer is",
        type: "text",
        required: true,
        placeholder: "Building, floor, and nearest room",
        whyItMatters:
          "Internal-plane work needs the physical location to identify the device and its print server, and §8.6 forbids guessing an appliance address.",
      },
      {
        id: "printer-problem",
        label: "What is wrong",
        type: "select",
        required: true,
        options: [
          "Nothing prints",
          "The printer is missing from my list",
          "An error on the printer display",
          "Print quality",
          "Scan to email",
        ],
        whyItMatters:
          "Separates a queue or driver problem on one machine from a device fault everyone shares, which is the same distinction the merge decision turns on.",
      },
      {
        id: "display-or-error-text",
        label: "The exact wording on the printer or on screen",
        type: "textarea",
        required: false,
        whyItMatters:
          "Panel codes are exact identifiers a technician can act on directly, and they are allowlisted shapes at intake, so they can be pasted verbatim.",
      },
      {
        id: "others-affected",
        label: "Who else is affected",
        type: "select",
        required: true,
        options: ["Only me", "Others on my floor too", "Unknown"],
        whyItMatters:
          "Normalizes to the §8.2 impact statement and is the requester-side evidence behind the reuse decision the applier makes.",
      },
    ],
    wikiSopHints: [],
    identifiersExpected: ["printer", "deviceName", "server", "upn", "errorText"],
  },
  {
    id: "new-starter-onboarding",
    label: "New starter",
    description:
      "Someone is joining and needs an account, access, and equipment before they start.",
    accessPlane: "mixed",
    portalReceivable: true,
    alwaysEscalate: true,
    escalationReason:
      "Creating an account and granting its group membership is administrator and permission work, which §8.12 stops on. It is also HR-context work under §8.4, so neither the draft nor the merge decision is the agent's to finish.",
    mergePolicy: "human-confirms-merge",
    defaultAutonomy: INITIAL_AUTONOMY,
    followUpFields: [
      {
        id: "starter-name",
        label: "Full name of the new starter",
        type: "text",
        required: true,
        whyItMatters:
          "The display name is the identifier every account, licence, and device assignment is created against, and the one thing that must not be approximated.",
      },
      {
        id: "start-date",
        label: "Start date",
        type: "date",
        required: true,
        whyItMatters:
          "The deadline the acceptance criteria and the stop condition are written against. Provisioning lead times only mean something relative to it.",
      },
      {
        id: "department-and-role",
        label: "Department and role",
        type: "text",
        required: true,
        whyItMatters:
          "The role determines the access set. Without it the card cannot state a group membership, and the permission work has nothing to be approved against.",
      },
      {
        id: "set-up-like-whom",
        label: "“Set up like…” — name the person, if that is how you would describe it",
        type: "text",
        required: false,
        whyItMatters:
          "`Resolving-Set-Up-Like-Requests` exists for this phrasing. It is used to derive an explicit access list for review, never to clone an account — cloning privileged access is forbidden.",
      },
      {
        id: "equipment-needed",
        label: "Equipment needed",
        type: "select",
        required: true,
        options: [
          "Mac laptop",
          "Windows laptop",
          "Desktop",
          "No new device — reusing an existing one",
        ],
        whyItMatters:
          "Decides whether a provisioning child Task is needed and which plane it sits on, which is the §8.7 shape decision made before drafting.",
      },
      {
        id: "sponsor-or-approver",
        label: "Who approves the access",
        type: "text",
        required: true,
        whyItMatters:
          "Permission work needs a named approver on the card as an explicit prerequisite before any group membership is granted.",
      },
    ],
    wikiSopHints: ["Resolving-Set-Up-Like-Requests", "Mac-Provisioning-SOP"],
    identifiersExpected: ["displayName", "upn", "groupName", "deviceSerial"],
  },
  {
    id: "leaver-offboarding",
    label: "Leaver or offboarding",
    description: "Someone is leaving and their access, mailbox, files, and devices need handling.",
    accessPlane: "mixed",
    portalReceivable: true,
    alwaysEscalate: true,
    escalationReason:
      "Disabling an account, revoking sessions, and reassigning data are privileged and irreversible, and an immediate departure is the time-sensitive revocation §8.5 reserves Priority 1 for. §8.12 stops the agent on all three, and §8.13 forbids it from remediating in any case.",
    mergePolicy: "human-confirms-merge",
    defaultAutonomy: INITIAL_AUTONOMY,
    p1Candidate: true,
    followUpFields: [
      {
        id: "leaver-upn",
        label: "Sign-in address of the person leaving",
        type: "identifier",
        required: true,
        whyItMatters:
          "The account every revocation and audit test is written against. Disabling the wrong one is the irreversible mistake this category exists to prevent.",
      },
      {
        id: "last-working-day",
        label: "Last working day",
        type: "date",
        required: true,
        whyItMatters:
          "The revocation time is the acceptance criterion. Without it the card cannot state when access must be gone and the audit test has nothing to check.",
      },
      {
        id: "urgency",
        label: "How the departure should be handled",
        type: "select",
        required: true,
        options: ["Planned departure", "Immediate — access must be removed today"],
        whyItMatters:
          "An immediate revocation is the time-sensitive case §8.5 reserves Priority 1 for. A requester cannot set priority, so this is the input triage prices it from.",
      },
      {
        id: "mailbox-and-files-recipient",
        label: "Who receives their mailbox and files",
        type: "text",
        required: true,
        whyItMatters:
          "Reassignment is a separate observable action with its own approval; the card needs the destination named rather than discovered after the account is already disabled.",
      },
      {
        id: "devices-to-recover",
        label: "Devices to be returned, with serials if known",
        type: "textarea",
        required: true,
        whyItMatters:
          "Serials are §8.2 identifiers and the completion record for recovery. An unreturned device is the part of offboarding that otherwise silently never closes.",
      },
      {
        id: "approver",
        label: "Who confirms this departure",
        type: "text",
        required: true,
        whyItMatters:
          "An irreversible identity change needs a named approver recorded as a prerequisite. Nobody should be disabled on the strength of a portal form alone.",
      },
    ],
    wikiSopHints: [],
    identifiersExpected: ["upn", "displayName", "deviceSerial", "groupName", "path"],
  },
  {
    id: "network-wifi",
    label: "Network or Wi-Fi",
    description:
      "You cannot get onto the network, or the connection keeps dropping, in the office or at home.",
    accessPlane: "internal",
    portalReceivable: true,
    alwaysEscalate: false,
    mergePolicy: "agent-may-reuse",
    defaultAutonomy: INITIAL_AUTONOMY,
    p1Candidate: true,
    followUpFields: [
      {
        id: "location",
        label: "Office, floor, and nearest room or desk",
        type: "text",
        required: true,
        whyItMatters:
          "The internal plane needs a physical location to identify the switch, access point, or port. §8.6 forbids guessing an appliance address, so the location is what makes the card executable.",
      },
      {
        id: "connection-type",
        label: "Wired or Wi-Fi",
        type: "select",
        required: true,
        options: ["Wired", "Wi-Fi", "Both"],
        whyItMatters:
          "Wired and wireless are different equipment with different owners; the answer decides which prerequisites and which control plane the card names.",
      },
      {
        id: "who-is-affected",
        label: "Who else is affected",
        type: "select",
        required: true,
        options: ["Only me", "Several people near me", "The whole floor or office", "Unknown"],
        whyItMatters:
          "Normalizes to the §8.2 impact statement, and a whole-office answer is both what makes this a Priority 1 candidate and why two reports should become one card rather than two.",
      },
      {
        id: "device-name-or-serial",
        label: "Device name or serial number",
        type: "identifier",
        required: true,
        whyItMatters:
          "Even for a shared fault the card needs one device named exactly, because that is what the functional test is run against.",
      },
      {
        id: "what-you-cannot-reach",
        label: "What you cannot reach",
        type: "textarea",
        required: true,
        placeholder: "A specific site or share, or nothing at all",
        whyItMatters:
          "Separates a name-resolution problem from a plane problem from a full outage — three different Epics under §8.5, decided by this one answer.",
      },
    ],
    wikiSopHints: [],
    identifiersExpected: ["deviceName", "deviceSerial", "server", "path", "errorText"],
  },
  {
    id: "security-concern",
    label: "Suspicious message or security concern",
    description:
      "You have had a suspicious message, clicked something you should not have, or think an account has been compromised.",
    accessPlane: "cloud",
    portalReceivable: true,
    alwaysEscalate: true,
    escalationReason:
      "A possible compromise implies immediate session revocation and a credential change — privileged, irreversible, and time-sensitive, which is what §8.5 reserves Priority 1 for. §8.12 stops the agent on every one of those, and §8.13 forbids it from remediating.",
    mergePolicy: "human-confirms-merge",
    defaultAutonomy: INITIAL_AUTONOMY,
    p1Candidate: true,
    followUpFields: [
      {
        id: "what-happened",
        label: "What happened",
        type: "select",
        required: true,
        options: [
          "I received a suspicious message",
          "I clicked a link or opened an attachment",
          "I entered my password somewhere I should not have",
          "I think someone else is using my account",
          "Something else",
        ],
        whyItMatters:
          "Decides whether this card is a report or an active incident. Entering a password anywhere is an immediate revocation, which is the Priority 1 case in §8.5 rather than a queued piece of work.",
      },
      {
        id: "when-it-happened",
        label: "When, as precisely as you can",
        type: "text",
        required: true,
        whyItMatters:
          "The revocation window and the sign-in audit are written against the time. Approximate is fine; absent means the card cannot say what to check.",
      },
      {
        id: "account-affected",
        label: "The account involved",
        type: "identifier",
        required: true,
        whyItMatters:
          "The account is what sessions are revoked on and what the audit is run against, so it has to be exact.",
      },
      {
        id: "device-name-or-serial",
        label: "The device you were using",
        type: "identifier",
        required: true,
        whyItMatters:
          "If a link was opened the device is in scope for the endpoint check; the card names it by serial rather than describing it.",
      },
      {
        id: "sender-or-source",
        label: "The sender address or source, if you have it",
        type: "text",
        required: false,
        placeholder: "Type the address — please do not forward the message",
        whyItMatters:
          "The sender is an exact identifier the card can act on. Forwarding the message would carry live malicious content into the portal, which the intake rules and §2.3 do not allow.",
      },
      {
        id: "password-changed-since",
        label: "Have you changed your password since this happened?",
        type: "boolean",
        required: true,
        whyItMatters:
          "Whether the credential has already been rotated changes the first step entirely, and it is the prerequisite the card has to state before anyone else acts.",
      },
    ],
    wikiSopHints: [],
    identifiersExpected: ["upn", "displayName", "deviceName", "path", "errorText"],
  },
  {
    id: "something-else",
    label: "Something else",
    description: "Your request does not fit any of the other categories.",
    accessPlane: "unknown",
    portalReceivable: true,
    alwaysEscalate: false,
    mergePolicy: "human-confirms-merge",
    defaultAutonomy: INITIAL_AUTONOMY,
    followUpFields: [
      {
        id: "what-you-are-trying-to-do",
        label: "What you are trying to do, and what happens instead",
        type: "textarea",
        required: true,
        whyItMatters:
          "The requested outcome has to be unambiguous. With no category to shape the questions this is the only field carrying it, so the gate stands or falls on it.",
      },
      {
        id: "system-or-application",
        label: "The system or application involved",
        type: "identifier",
        required: true,
        whyItMatters:
          "Without a named system the access plane cannot be classified, and §8.5 makes the plane decide the Epic parent — an unclassified card has nowhere to be parented.",
      },
      {
        id: "who-is-affected",
        label: "Who is affected",
        type: "select",
        required: true,
        options: ["Only me", "Several people", "The whole company", "Unknown"],
        whyItMatters:
          "Normalizes to the §8.2 impact statement, which is an input to priority that the requester never sets directly.",
      },
      {
        id: "exact-error-text",
        label: "The exact wording of any error",
        type: "textarea",
        required: false,
        whyItMatters:
          "Verbatim text is often the only thing that lets an uncategorized request be classified at all.",
      },
      {
        id: "needed-by",
        label: "Needed by",
        type: "date",
        required: false,
        whyItMatters:
          "Gives triage a constraint to prioritize against without the requester setting priority (§8.5).",
      },
    ],
    wikiSopHints: [],
    identifiersExpected: ["applicationVersion", "deviceName", "path", "errorText"],
  },
] as const satisfies readonly RequestCategory[];

export type RequestCategoryId = (typeof REQUEST_CATEGORIES)[number]["id"];

/** The configured category, or `undefined` for an id that is not configured. */
export function getCategory(id: string): RequestCategory | undefined {
  return REQUEST_CATEGORIES.find((category) => category.id === id);
}

/** The categories the public intake form offers. */
export function portalReceivableCategories(): readonly RequestCategory[] {
  return REQUEST_CATEGORIES.filter((category) => category.portalReceivable);
}

/**
 * The §7.7 classes the portal can never receive. They exist for the staff-raised route (§11.3),
 * and the published guide sends them to the phone with `PHONE_ROUTE_SENTENCE`.
 */
export function phoneOnlyCategories(): readonly RequestCategory[] {
  return REQUEST_CATEGORIES.filter((category) => !category.portalReceivable);
}
